/**
 * The signed-in staff member — the admin half of the kiosk.
 *
 * Storage, deliberately: **sessionStorage**, not localStorage.
 *
 *   - The device token (DeviceContext) is the door kiosk's identity. It is
 *     persistent by design: the tablet reboots and keeps scanning.
 *   - This is a full-privilege portal JWT. It must never outlive the tab it was
 *     typed into, so the lobby device's persistent storage never holds one —
 *     close the tab and the session is gone (docs/decisions.md D-006).
 *
 * On top of that, the session signs itself out after 30 minutes without
 * interaction, because a staff member who walks away from a lobby machine is
 * indistinguishable from one who never came back.
 *
 * Cross-tab sync is intentionally absent: sessionStorage is per-tab, and that
 * isolation is the point. Two tabs are two sessions.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "../api/client";
import { fetchStaffIdentity, login } from "../api/kiosk";

const STORAGE_KEY = "zebra-kiosk-staff";

/** Idle window. Long enough to read a class list, short enough to bound a walk-away. */
const IDLE_LIMIT_MS = 30 * 60_000;
/** How often the idle sweep runs. A minute of slack on a 30-minute limit is nothing. */
const IDLE_SWEEP_MS = 60_000;
/** Activity is noisy; one write per half-minute is plenty to keep the session warm. */
const ACTIVITY_THROTTLE_MS = 30_000;

const IDLE_MESSAGE = "Signed out after 30 minutes of inactivity — sign in again.";
const EXPIRED_MESSAGE = "Session expired — sign in again.";

export interface StaffSession {
  token: string;
  userId: number;
  /** First name, for the "signed in as" line. Never the email. */
  displayName: string;
  /** Epoch ms. */
  signedInAt: number;
  /** Epoch ms; bumped by the activity listener, not rendered anywhere. */
  lastActiveAt: number;
}

interface StaffContextValue {
  staff: StaffSession | null;
  isSignedIn: boolean;
  /** Why the last session ended, when it ended on its own. Shown above the form. */
  expiredReason: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: (reason?: string) => void;
  /**
   * Hand a caught error here first. A 401 on a staff call means the JWT is dead,
   * so the session is ended and the login gate explains why. Returns true when
   * it handled the error, so callers can skip their own message.
   */
  handleApiError: (caught: unknown) => boolean;
}

const StaffContext = createContext<StaffContextValue | null>(null);

interface StoredRead {
  staff: StaffSession | null;
  /** True when a stored session was found but had already gone stale. */
  idled: boolean;
}

function readStoredStaff(): StoredRead {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage disabled by policy. Treat as signed out — the login form works,
    // it just won't survive a reload, which is visible and honest.
    return { staff: null, idled: false };
  }
  if (!raw) return { staff: null, idled: false };

  let parsed: Partial<StaffSession> | null;
  try {
    parsed = JSON.parse(raw) as Partial<StaffSession>;
  } catch {
    parsed = null;
  }
  if (
    !parsed ||
    typeof parsed.token !== "string" ||
    !parsed.token ||
    typeof parsed.userId !== "number"
  ) {
    clearStoredStaff();
    return { staff: null, idled: false };
  }

  const lastActiveAt = typeof parsed.lastActiveAt === "number" ? parsed.lastActiveAt : 0;
  if (Date.now() - lastActiveAt > IDLE_LIMIT_MS) {
    // The tab was reloaded after a long sleep. Same outcome as the sweep.
    clearStoredStaff();
    return { staff: null, idled: true };
  }

  return {
    staff: {
      token: parsed.token,
      userId: parsed.userId,
      displayName: typeof parsed.displayName === "string" ? parsed.displayName : "",
      signedInAt:
        typeof parsed.signedInAt === "number" ? parsed.signedInAt : lastActiveAt,
      lastActiveAt,
    },
    idled: false,
  };
}

function persistStaff(session: StaffSession): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Keep the session in memory anyway; a reload sends staff back to the form.
  }
}

function clearStoredStaff(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing useful to do */
  }
}

export function StaffProvider({ children }: { children: ReactNode }) {
  const [initial] = useState(readStoredStaff);
  const [staff, setStaff] = useState<StaffSession | null>(initial.staff);
  const [expiredReason, setExpiredReason] = useState<string | null>(
    initial.idled ? IDLE_MESSAGE : null,
  );

  /*
    `lastActiveAt` changes every 30 seconds while someone is working. Routing
    that through state would re-render every admin screen (including a sheet of
    a hundred QR codes) for a value nothing renders, so the live copy lives in
    refs and in sessionStorage. The state object's `lastActiveAt` is therefore
    only accurate as of the last sign-in — read the session's freshness from
    storage, never from the rendered value.
  */
  const staffRef = useRef<StaffSession | null>(initial.staff);
  const lastActiveRef = useRef<number>(initial.staff?.lastActiveAt ?? 0);

  const isSignedIn = staff !== null;

  const signOut = useCallback((reason?: string) => {
    clearStoredStaff();
    staffRef.current = null;
    lastActiveRef.current = 0;
    setStaff(null);
    setExpiredReason(reason ?? null);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const token = await login(email.trim(), password);
    const identity = await fetchStaffIdentity(token);
    const now = Date.now();
    const next: StaffSession = {
      token,
      userId: identity.userId,
      displayName: identity.firstname || identity.lastname || "Staff",
      signedInAt: now,
      lastActiveAt: now,
    };
    persistStaff(next);
    staffRef.current = next;
    lastActiveRef.current = now;
    setStaff(next);
    setExpiredReason(null);
  }, []);

  const handleApiError = useCallback(
    (caught: unknown): boolean => {
      const unauthenticated =
        caught instanceof ApiError &&
        (caught.httpStatus === 401 || caught.status === "unauthenticated");
      if (unauthenticated) signOut(EXPIRED_MESSAGE);
      return unauthenticated;
    },
    [signOut],
  );

  // Sliding window: any real interaction counts, throttled so a mouse drag is
  // one write rather than three hundred.
  useEffect(() => {
    if (!isSignedIn) return;
    function bump() {
      const now = Date.now();
      if (now - lastActiveRef.current < ACTIVITY_THROTTLE_MS) return;
      const current = staffRef.current;
      if (!current) return;
      lastActiveRef.current = now;
      const next = { ...current, lastActiveAt: now };
      staffRef.current = next;
      persistStaff(next);
    }
    window.addEventListener("pointerdown", bump, { passive: true });
    window.addEventListener("keydown", bump, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", bump);
      window.removeEventListener("keydown", bump);
    };
  }, [isSignedIn]);

  useEffect(() => {
    if (!isSignedIn) return;
    const timer = window.setInterval(() => {
      if (Date.now() - lastActiveRef.current > IDLE_LIMIT_MS) signOut(IDLE_MESSAGE);
    }, IDLE_SWEEP_MS);
    return () => window.clearInterval(timer);
  }, [isSignedIn, signOut]);

  const value = useMemo<StaffContextValue>(
    () => ({ staff, isSignedIn, expiredReason, signIn, signOut, handleApiError }),
    [staff, isSignedIn, expiredReason, signIn, signOut, handleApiError],
  );

  return <StaffContext.Provider value={value}>{children}</StaffContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStaff(): StaffContextValue {
  const context = useContext(StaffContext);
  if (!context) {
    throw new Error("useStaff must be used inside <StaffProvider>");
  }
  return context;
}
